import React, { useState } from "react";
import type { UIMessage } from "@ai-sdk/react";
import {
  ChevronUp,
  MessageCircle,
  Mic,
  PanelRightClose,
  PanelRightOpen,
  Volume2,
  VolumeX,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useAgentChatStore } from "../store/useAgentChatStore";
import { CompanionChatLayout } from "./CompanionChatLayout";
import { CompanionDockAvatarMedia } from "./CompanionDockAvatarMedia";
import type { CompanionInputProps } from "./agent-chat/CompanionInput";
import type { AvatarHandle } from "./agent-chat/types";
import type { CompanionTokenUsageSnapshot } from "../lib/companionChatRuntime";

export interface CompanionPanelPersona {
  humanName: string;
  humanRole: string;
  humanVideo: string;
  colorHex: string;
  voiceId: string;
  simliFaceId?: string;
  liveAvatarId?: string;
}

export interface CompanionPanelRenderProps {
  persona: CompanionPanelPersona;
  avatarRef: React.RefObject<AvatarHandle | null>;
  avatarProvider: string;
  avatarConnected: boolean;
  avatarConnecting: boolean;
  avatarSpeaking: boolean;
  avatarMuted: boolean;
  voiceVolume: number;
  setVoiceVolume: (v: number) => void;
  hasValidAvatarId: boolean;
  avatarStatusLabel: string;
  avatarStatusDotClass: string;
  setAvatarConnected: (c: boolean) => void;
  setAvatarConnecting: (c: boolean) => void;
  setAvatarMuted: (m: boolean) => void;
  scheduleAvatarSpeakingStart: () => void;
  scheduleAvatarSpeakingStop: () => void;
  isListening: boolean;
  isSecureMicContext: boolean;
  micHint: string | null;
  toggleListen: () => void;
  activeMessages: UIMessage[];
  inputProps: CompanionInputProps;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  voiceTranscriptRef?: React.RefObject<string>;
  isLoading?: boolean;
  isCompacting?: boolean;
  tokenUsage?: CompanionTokenUsageSnapshot | null;
}

export type CompanionPanelRenderer = React.ComponentType<CompanionPanelRenderProps>;

const DESKTOP_DOCK_WIDTH = "clamp(328px, 26vw, 400px)";

interface CompanionDockShellProps {
  agentName: string;
  isMobile: boolean;
  children: React.ReactNode;
  className?: string;
  persona?: CompanionPanelPersona;
}

export function CompanionDockToggleButton({
  agentName,
  collapsed,
  className,
  iconClassName,
  style,
  children,
}: {
  agentName: string;
  collapsed: boolean;
  className?: string;
  iconClassName?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}) {
  const setHidden = useAgentChatStore((s) => s.setDockedPanelHidden);
  const Icon = collapsed ? PanelRightOpen : PanelRightClose;
  const label = collapsed ? `Open ${agentName} side chat` : `Hide ${agentName} side chat`;

  return (
    <button
      type="button"
      onClick={() => setHidden(!collapsed)}
      aria-label={label}
      title={label}
      className={className}
      style={style}
    >
      {children ?? <Icon className={iconClassName} />}
    </button>
  );
}

function DockedPanelReopenFab({
  agentName,
  persona,
}: {
  agentName: string;
  persona?: CompanionPanelPersona;
}) {
  return (
    <CompanionDockToggleButton
      agentName={agentName}
      collapsed
      className="group fixed bottom-[calc(4rem+env(safe-area-inset-bottom,0px)+1rem)] right-4 z-50 flex h-14 w-14 items-center justify-center overflow-hidden rounded-full bg-card/70 text-foreground shadow-ambient-lg backdrop-blur-[16px] transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-card active:scale-95 sm:right-5 md:bottom-6"
    >
      <span
        aria-hidden="true"
        className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_30%_18%,hsl(var(--primary)/0.22),transparent_46%),linear-gradient(135deg,hsl(var(--surface-container-highest)/0.78),hsl(var(--card)/0.42))]"
      />
      <span
        aria-hidden="true"
        className="absolute inset-1 overflow-hidden rounded-full bg-surface-low/75 backdrop-blur-[16px] transition-colors group-hover:bg-surface-container/90"
      >
        <CompanionDockAvatarMedia
          persona={persona}
          mediaClassName="scale-[1.18]"
          iconClassName="h-5 w-5"
        />
      </span>
      <span
        aria-hidden="true"
        className="absolute bottom-1.5 right-1.5 z-20 flex h-4 w-4 items-center justify-center rounded-full bg-card/90 text-muted-foreground shadow-ambient"
      >
        <PanelRightOpen className="h-2.5 w-2.5" />
      </span>
    </CompanionDockToggleButton>
  );
}

export function CompanionDockShell({
  agentName,
  isMobile,
  children,
  className,
  persona,
}: CompanionDockShellProps) {
  const dockedPanelHidden = useAgentChatStore((s) => s.dockedPanelHidden);

  if (dockedPanelHidden) return <DockedPanelReopenFab agentName={agentName} persona={persona} />;

  if (isMobile) {
    return (
      <div
        className={cn(
          "relative z-30 flex w-full shrink-0 flex-col overflow-hidden bg-card/75 shadow-ambient-lg backdrop-blur-[16px]",
          className,
        )}
      >
        {children}
      </div>
    );
  }

  return (
    <>
      <CompanionDockToggleButton
        agentName={agentName}
        collapsed={false}
        className="fixed top-1/2 z-50 hidden h-12 w-9 -translate-y-1/2 items-center justify-center rounded-l-full bg-card/80 text-muted-foreground shadow-ambient-lg backdrop-blur-[16px] transition-[background-color,color,width] duration-200 hover:w-11 hover:bg-card hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:flex"
        iconClassName="h-4 w-4"
        style={{ right: DESKTOP_DOCK_WIDTH }}
      />
      <div
        className={cn(
          "relative z-30 flex h-full shrink-0 flex-col overflow-hidden bg-card/75 shadow-ambient-lg backdrop-blur-[16px]",
          className,
        )}
        style={{ width: DESKTOP_DOCK_WIDTH }}
      >
        <div className="min-h-0 flex flex-1 flex-col">{children}</div>
      </div>
    </>
  );
}

export function DefaultCompanionDockPanel({
  persona,
  avatarRef,
  avatarProvider,
  avatarConnected,
  avatarConnecting,
  avatarSpeaking,
  avatarMuted,
  voiceVolume,
  setVoiceVolume,
  hasValidAvatarId,
  avatarStatusLabel,
  avatarStatusDotClass,
  setAvatarConnected,
  setAvatarConnecting,
  setAvatarMuted,
  scheduleAvatarSpeakingStart,
  scheduleAvatarSpeakingStop,
  isListening,
  isSecureMicContext,
  micHint,
  toggleListen,
  activeMessages,
  inputProps,
  voiceTranscriptRef,
  isLoading,
  isCompacting,
  tokenUsage,
}: CompanionPanelRenderProps) {
  const isMobile = useIsMobile();
  const [mobileBottomExpanded, setMobileBottomExpanded] = useState(false);

  const chatLayout = (
    <CompanionChatLayout
      persona={persona}
      activeMessages={activeMessages}
      inputProps={inputProps}
      isLoading={isLoading}
      isCompacting={isCompacting}
      tokenUsage={tokenUsage}
      isListening={isListening}
      isSecureMicContext={isSecureMicContext}
      avatarMuted={avatarMuted}
      avatarSpeaking={avatarSpeaking}
      voiceVolume={voiceVolume}
      setVoiceVolume={setVoiceVolume}
      avatarHero={{
        avatarRef,
        avatarProvider,
        avatarConnected,
        avatarConnecting,
        hasValidAvatarId,
        liveAvatarId: persona.liveAvatarId,
        simliFaceId: persona.simliFaceId,
        voiceId: persona.voiceId,
        setAvatarConnected,
        setAvatarConnecting,
        scheduleAvatarSpeakingStart,
        scheduleAvatarSpeakingStop,
      }}
      toggleListen={toggleListen}
      setAvatarMuted={setAvatarMuted}
      voiceTranscriptRef={voiceTranscriptRef}
    />
  );

  if (!isMobile) {
    return (
      <CompanionDockShell agentName={persona.humanName} isMobile={false} persona={persona}>
        {chatLayout}
      </CompanionDockShell>
    );
  }

  return (
    <CompanionDockShell agentName={persona.humanName} isMobile persona={persona}>
      <div className="px-4 pt-3 pb-2 flex items-center gap-3">
        <div className="relative shrink-0">
          {(avatarSpeaking || isListening) && (
            <div
              className="absolute -inset-1.5 rounded-full pointer-events-none animate-pulse"
              style={{
                background: isListening
                  ? "radial-gradient(circle, rgba(239,68,68,0.2), transparent 70%)"
                  : `radial-gradient(circle, ${persona.colorHex}30, transparent 70%)`,
                animationDuration: "700ms",
              }}
            />
          )}
          <div
            className={cn(
              "relative z-10 flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-surface-high text-foreground shadow-ambient",
              isListening
                ? "ring-2 ring-red-400/40"
                : avatarSpeaking
                  ? "ring-2 ring-primary/40"
                  : avatarConnected
                    ? "ring-2 ring-primary/20"
                    : "ring-1 ring-outline-variant/20",
            )}
          >
            <span aria-hidden="true" className="absolute inset-0" />
            <CompanionDockAvatarMedia
              persona={persona}
              mediaClassName="scale-[1.18]"
              iconClassName="h-4 w-4"
            />
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-[9px] uppercase tracking-[0.2em] text-primary font-bold mb-1 truncate">
            {persona.humanRole}
          </p>
          <div className="flex items-center gap-1.5">
            <div className={cn("w-1.5 h-1.5 rounded-full", avatarStatusDotClass)} />
            <span className="truncate text-xs font-semibold text-foreground">
              {persona.humanName}
            </span>
            <span className="text-[9px] text-muted-foreground uppercase tracking-wider font-medium">
              {avatarStatusLabel}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => {
              if (isSecureMicContext) toggleListen();
            }}
            disabled={!isSecureMicContext && !isListening}
            title={micHint || "Toggle Microphone"}
            className={cn(
              "w-8 h-8 flex items-center justify-center rounded-full transition-all",
              isListening
                ? "bg-red-500 text-white shadow-red-500/30 ring-2 ring-red-500/20"
                : !isSecureMicContext
                  ? "bg-muted/60 text-muted-foreground/30 cursor-not-allowed"
                  : "bg-background text-foreground",
            )}
          >
            <Mic className={cn("w-3.5 h-3.5", isListening && "animate-pulse")} />
          </button>
          <button
            type="button"
            onClick={() => setAvatarMuted(!avatarMuted)}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-background text-foreground"
          >
            {avatarMuted ? (
              <VolumeX className="w-3.5 h-3.5" />
            ) : (
              <Volume2 className="w-3.5 h-3.5" />
            )}
          </button>
          <button
            type="button"
            onClick={() => setMobileBottomExpanded((expanded) => !expanded)}
            className={cn(
              "w-8 h-8 flex items-center justify-center rounded-full transition-all",
              mobileBottomExpanded ? "bg-primary/15 text-primary" : "bg-background text-foreground",
            )}
          >
            {mobileBottomExpanded ? (
              <ChevronUp className="w-3.5 h-3.5" />
            ) : (
              <MessageCircle className="w-3.5 h-3.5" />
            )}
          </button>
          <CompanionDockToggleButton
            agentName={persona.humanName}
            collapsed={false}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-background text-muted-foreground hover:text-foreground transition-colors"
            iconClassName="w-3.5 h-3.5"
          />
        </div>
      </div>

      {mobileBottomExpanded && (
        <div className="bg-card/30 flex flex-col min-h-0 max-h-[calc(60vh-4rem-env(safe-area-inset-bottom,0px))] pb-safe-mobile-nav animate-in slide-in-from-top-2 duration-200">
          {chatLayout}
        </div>
      )}
    </CompanionDockShell>
  );
}
